import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import LinearProgress from '@mui/material/LinearProgress'
import Typography from '@mui/material/Typography'

import { makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'

type CostToServe = {
  window_days: number
  summary: {
    total_cost_gbp: number
    total_hours: number
    jobs_completed: number
    avg_cost_per_job_gbp: number
    subcontractor_share_gbp: number
    subcontractor_share_pct: number
    overtime_cost_gbp: number
    overtime_share_pct: number
    attendance_pct: number
  }
  by_tier: Array<{
    tier: string; display_name: string; workforce_class: 'internal' | 'subcontractor' | 'partner'
    engineers: number; jobs: number; hours: number; cost_gbp: number
    overtime_hours: number; overtime_cost_gbp: number; cost_per_job_gbp: number
  }>
  by_patch: Array<{
    patch: string; jobs: number; hours: number; cost_gbp: number; cost_per_job_gbp: number
    internal_pct: number; subcontractor_pct: number; partner_pct: number
  }>
  by_job_type_tier: Array<{
    job_type: string; tier: string; jobs: number; avg_cost_gbp: number; avg_hours: number
  }>
  overtime_leaderboard: Array<{
    engineer_id: string; display_name: string; external_ref: string; tier: string
    overtime_hours: number; overtime_cost_gbp: number; attended_shifts: number
  }>
  attendance_watchlist: Array<{
    engineer_id: string; display_name: string; external_ref: string; tier: string
    attendance_pct: number; attended_shifts: number; sick_shifts: number
    holiday_shifts: number; absent_shifts: number; total_shifts: number
  }>
}

const WINDOWS = [7, 30, 90] as const
type WindowDays = (typeof WINDOWS)[number]

export default function CostPage() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])
  const [window, setWindow] = useState<WindowDays>(30)

  const q = useQuery({
    queryKey: ['analytics', 'cost-to-serve', window] as const,
    queryFn: () => client.call<CostToServe>(`/api/v1/analytics/cost-to-serve?window_days=${window}`),
    refetchInterval: 60_000,
  })

  // Normalize null arrays + null summary numerics coming back from a sparse
  // dataset so `.length`, `.map` and `.toFixed` never see null. Backend
  // should emit [] and 0 instead of null; this frontend guard keeps the
  // page rendering while that fix ships.
  const data = useMemo<CostToServe | undefined>(() => {
    if (!q.data) return undefined
    const s = q.data.summary ?? ({} as CostToServe['summary'])
    return {
      window_days: q.data.window_days ?? 0,
      summary: {
        total_cost_gbp:          s.total_cost_gbp ?? 0,
        total_hours:             s.total_hours ?? 0,
        jobs_completed:          s.jobs_completed ?? 0,
        avg_cost_per_job_gbp:    s.avg_cost_per_job_gbp ?? 0,
        subcontractor_share_gbp: s.subcontractor_share_gbp ?? 0,
        subcontractor_share_pct: s.subcontractor_share_pct ?? 0,
        overtime_cost_gbp:       s.overtime_cost_gbp ?? 0,
        overtime_share_pct:      s.overtime_share_pct ?? 0,
        attendance_pct:          s.attendance_pct ?? 0,
      },
      by_tier: q.data.by_tier ?? [],
      by_patch: q.data.by_patch ?? [],
      by_job_type_tier: q.data.by_job_type_tier ?? [],
      overtime_leaderboard: q.data.overtime_leaderboard ?? [],
      attendance_watchlist: q.data.attendance_watchlist ?? [],
    }
  }, [q.data])

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Window
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={window}
          onChange={(_, v) => v && setWindow(v as WindowDays)}
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none' } }}
        >
          {WINDOWS.map((n) => (
            <ToggleButton key={n} value={n}>{n} d</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>

        {q.isLoading && <LinearProgress />}
        {q.isError && (
          <Alert severity="error">{q.error instanceof Error ? q.error.message : 'Failed to load.'}</Alert>
        )}
        {data && data.summary.jobs_completed === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No completed jobs in the last {data.window_days} days. Populate via the WFM adapter or run <code>go run ./cmd/seed</code>.
          </Alert>
        )}
        {data && (
          <Stack spacing={2}>
            <KpiStrip data={data} />
            <TierBreakdown data={data} />
            <PatchBreakdown data={data} />
            <JobTypeTierGrid data={data} />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}><OvertimeTable rows={data.overtime_leaderboard} /></Box>
              <Box sx={{ flex: 1 }}><AttendanceTable rows={data.attendance_watchlist} /></Box>
            </Stack>
          </Stack>
        )}
    </Container>
  )
}

// --- widgets --------------------------------------------------------------

function KpiStrip({ data }: { data: CostToServe }) {
  const s = data.summary
  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
        <Kpi label={`Total spend (${data.window_days}d)`} value={formatGBP(s.total_cost_gbp)} tone="primary" />
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <Kpi label="Avg £ / visit" value={formatGBP(s.avg_cost_per_job_gbp)} />
        <Kpi label="Jobs completed" value={s.jobs_completed.toLocaleString()} />
        <Kpi label="Hours worked" value={s.total_hours.toFixed(0)} />
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <Kpi label="Subcontractor share" value={`${s.subcontractor_share_pct.toFixed(1)}%`}
             tone={s.subcontractor_share_pct >= 50 ? 'warning' : 'default'} />
        <Kpi label="Overtime share" value={`${s.overtime_share_pct.toFixed(1)}%`}
             tone={s.overtime_share_pct >= 10 ? 'warning' : 'default'} />
        <Kpi label="Attendance" value={`${s.attendance_pct.toFixed(1)}%`}
             tone={s.attendance_pct < 90 ? 'warning' : 'default'} />
      </Stack>
    </Paper>
  )
}

function Kpi({ label, value, tone = 'default' }: { label: string; value: string; tone?: 'default' | 'primary' | 'warning' }) {
  const color = tone === 'warning' ? 'warning.main' : tone === 'primary' ? 'primary.main' : 'text.primary'
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="h6" sx={{ color, fontWeight: 700 }}>{value}</Typography>
    </Box>
  )
}

function TierBreakdown({ data }: { data: CostToServe }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>By workforce tier</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tier</TableCell>
              <TableCell>Class</TableCell>
              <TableCell align="right">Engineers</TableCell>
              <TableCell align="right">Jobs</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">Total cost</TableCell>
              <TableCell align="right">£ / job</TableCell>
              <TableCell align="right">Overtime hrs</TableCell>
              <TableCell align="right">Overtime cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.by_tier.length === 0 && (
              <TableRow><TableCell colSpan={9} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                No completed jobs by tier in this window.
              </TableCell></TableRow>
            )}
            {data.by_tier.map((r) => (
              <TableRow key={r.tier} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.display_name}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>{r.tier}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={r.workforce_class} size="small"
                        color={r.workforce_class === 'internal' ? 'success' : r.workforce_class === 'partner' ? 'warning' : 'default'} />
                </TableCell>
                <TableCell align="right">{r.engineers}</TableCell>
                <TableCell align="right">{r.jobs.toLocaleString()}</TableCell>
                <TableCell align="right">{r.hours.toFixed(0)}</TableCell>
                <TableCell align="right"><b>{formatGBP(r.cost_gbp)}</b></TableCell>
                <TableCell align="right">{formatGBP(r.cost_per_job_gbp)}</TableCell>
                <TableCell align="right">{r.overtime_hours > 0 ? r.overtime_hours.toFixed(1) : '—'}</TableCell>
                <TableCell align="right">{r.overtime_cost_gbp > 0 ? formatGBP(r.overtime_cost_gbp) : '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

function PatchBreakdown({ data }: { data: CostToServe }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>By patch — with tier mix</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Patch</TableCell>
              <TableCell align="right">Jobs</TableCell>
              <TableCell align="right">Hours</TableCell>
              <TableCell align="right">Total cost</TableCell>
              <TableCell align="right">£ / job</TableCell>
              <TableCell sx={{ minWidth: 240 }}>Tier mix (by £ spend)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.by_patch.length === 0 && (
              <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                No completed jobs by patch in this window.
              </TableCell></TableRow>
            )}
            {data.by_patch.map((r) => (
              <TableRow key={r.patch} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.patch}</Typography>
                </TableCell>
                <TableCell align="right">{r.jobs.toLocaleString()}</TableCell>
                <TableCell align="right">{r.hours.toFixed(0)}</TableCell>
                <TableCell align="right"><b>{formatGBP(r.cost_gbp)}</b></TableCell>
                <TableCell align="right">{formatGBP(r.cost_per_job_gbp)}</TableCell>
                <TableCell>
                  <TierMixBar internal={r.internal_pct} sub={r.subcontractor_pct} partner={r.partner_pct} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

function TierMixBar({ internal, sub, partner }: { internal: number; sub: number; partner: number }) {
  return (
    <Box>
      <Box sx={{ display: 'flex', height: 12, borderRadius: 0.5, overflow: 'hidden', bgcolor: 'action.hover' }}>
        {internal > 0 && <Box sx={{ width: `${internal}%`, bgcolor: 'success.main' }} title={`Internal ${internal.toFixed(0)}%`} />}
        {sub > 0 && <Box sx={{ width: `${sub}%`, bgcolor: 'grey.500' }} title={`Subcontractor ${sub.toFixed(0)}%`} />}
        {partner > 0 && <Box sx={{ width: `${partner}%`, bgcolor: 'warning.main' }} title={`Partner ${partner.toFixed(0)}%`} />}
      </Box>
      <Stack direction="row" spacing={1.5} sx={{ mt: 0.5 }}>
        <Legend label="Internal" value={internal} color="success.main" />
        <Legend label="Sub" value={sub} color="grey.500" />
        <Legend label="Partner" value={partner} color="warning.main" />
      </Stack>
    </Box>
  )
}

function Legend({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
      <Box sx={{ width: 8, height: 8, bgcolor: color, borderRadius: '50%' }} />
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
        {label} {value.toFixed(0)}%
      </Typography>
    </Stack>
  )
}

function JobTypeTierGrid({ data }: { data: CostToServe }) {
  // Pivot: rows = job_type, cols = distinct tiers observed.
  const jobTypes = Array.from(new Set(data.by_job_type_tier.map((r) => r.job_type))).sort()
  const tiers = Array.from(new Set(data.by_job_type_tier.map((r) => r.tier))).sort()
  const byKey = new Map(data.by_job_type_tier.map((r) => [`${r.job_type}|${r.tier}`, r]))

  // Colour scale — find max avg_cost for red anchoring.
  const maxCost = Math.max(1, ...data.by_job_type_tier.map((r) => r.avg_cost_gbp))

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Unit economics · £ per visit by job type × tier</Typography>
      {jobTypes.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Nothing to grid yet.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Job type</TableCell>
                {tiers.map((t) => <TableCell key={t} align="right">{t}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {jobTypes.map((jt) => (
                <TableRow key={jt} hover>
                  <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{jt}</Typography></TableCell>
                  {tiers.map((t) => {
                    const cell = byKey.get(`${jt}|${t}`)
                    if (!cell) return <TableCell key={t} align="right" sx={{ color: 'text.secondary' }}>—</TableCell>
                    const intensity = Math.min(1, cell.avg_cost_gbp / maxCost)
                    // Lightweight amber-red tint: transparent → alert
                    const bg = `rgba(229, 80, 28, ${(intensity * 0.25).toFixed(2)})`
                    return (
                      <TableCell key={t} align="right" sx={{ bgcolor: bg }}>
                        <Typography variant="body2"><b>{formatGBP(cell.avg_cost_gbp)}</b></Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {cell.jobs} jobs · {cell.avg_hours.toFixed(1)}h
                        </Typography>
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

function OvertimeTable({ rows }: { rows: CostToServe['overtime_leaderboard'] }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Overtime — top drivers</Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>No overtime recorded in this window.</Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Engineer</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell align="right">OT hours</TableCell>
                <TableCell align="right">OT cost</TableCell>
                <TableCell align="right">Attended</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.engineer_id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.display_name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>{r.external_ref}</Typography>
                  </TableCell>
                  <TableCell><Chip label={r.tier} size="small" variant="outlined" /></TableCell>
                  <TableCell align="right"><b>{r.overtime_hours.toFixed(1)}</b></TableCell>
                  <TableCell align="right">{formatGBP(r.overtime_cost_gbp)}</TableCell>
                  <TableCell align="right">{r.attended_shifts}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

function AttendanceTable({ rows }: { rows: CostToServe['attendance_watchlist'] }) {
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Attendance watchlist &lt; 85%</Typography>
      {rows.length === 0 ? (
        <Alert severity="success" variant="outlined">
          No engineers below the 85% attendance threshold — the workforce is on the road.
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Engineer</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell align="right">Attendance</TableCell>
                <TableCell align="right">Attended</TableCell>
                <TableCell align="right">Sick</TableCell>
                <TableCell align="right">Absent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.engineer_id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.display_name}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>{r.external_ref}</Typography>
                  </TableCell>
                  <TableCell><Chip label={r.tier || '—'} size="small" variant="outlined" /></TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ color: r.attendance_pct < 70 ? 'error.main' : 'warning.main', fontWeight: 700 }}>
                      {r.attendance_pct.toFixed(0)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{r.attended_shifts}</TableCell>
                  <TableCell align="right">{r.sick_shifts}</TableCell>
                  <TableCell align="right">{r.absent_shifts}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

function formatGBP(n: number): string {
  if (Number.isNaN(n) || !Number.isFinite(n)) return '—'
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n)
}
